import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Moment from 'moment';
import { PreorderEventDetails } from '../../../actions/preorderEventActions/PreorderEventActionsTypes';

interface UpdatePreorderDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (updateData: {
    archived: boolean;
    name: string;
    lastPreorderDate: string;
    releaseDate: string;
  }) => void;
  preorderEventDetails: PreorderEventDetails | null;
  isLoading: boolean;
}

const UpdatePreorderDetailsDialog: React.FC<UpdatePreorderDetailsDialogProps> = ({
  open,
  onClose,
  onUpdate,
  preorderEventDetails,
  isLoading
}) => {
  const { t } = useTranslation('global');
  const [formData, setFormData] = useState({
    name: '',
    lastPreorderDate: '',
    releaseDate: ''
  });
  const [errors, setErrors] = useState({
    name: false,
    lastPreorderDate: false,
    releaseDate: false
  });

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open && preorderEventDetails) {
      setFormData({
        name: preorderEventDetails.name || '',
        lastPreorderDate: preorderEventDetails.lastPreorderDate 
          ? Moment(preorderEventDetails.lastPreorderDate).format('YYYY-MM-DD') 
          : '',
        releaseDate: preorderEventDetails.releaseDate 
          ? Moment(preorderEventDetails.releaseDate).format('YYYY-MM-DD') 
          : ''
      });
      setErrors({
        name: false,
        lastPreorderDate: false,
        releaseDate: false
      });
    }
  }, [open, preorderEventDetails]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      name: !formData.name.trim(),
      lastPreorderDate: !formData.lastPreorderDate,
      releaseDate: !formData.releaseDate
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleUpdate = () => {
    if (!validateForm()) {
      return;
    }

    onUpdate({
      archived: preorderEventDetails?.archived || false,
      name: formData.name.trim(),
      lastPreorderDate: formData.lastPreorderDate,
      releaseDate: formData.releaseDate
    });
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      disableBackdropClick={isLoading}
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle>
        {t('preorderEventDetails.update-details-dialog-title')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} style={{ marginTop: 8 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('common.name')}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={errors.name}
              helperText={errors.name ? t('common.field-required') : ''}
              disabled={isLoading}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={t('preorderEventDetails.last-preorder-date')}
              value={formData.lastPreorderDate}
              onChange={(e) => handleInputChange('lastPreorderDate', e.target.value)}
              error={errors.lastPreorderDate}
              helperText={errors.lastPreorderDate ? t('common.field-required') : ''}
              disabled={isLoading}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={t('preorderEventDetails.release-date')}
              value={formData.releaseDate}
              onChange={(e) => handleInputChange('releaseDate', e.target.value)}
              error={errors.releaseDate}
              helperText={errors.releaseDate ? t('common.field-required') : ''}
              disabled={isLoading}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleUpdate}
          color="primary"
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? t('preorderEventDetails.updating') : t('preorderEventDetails.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdatePreorderDetailsDialog;